import { defineMutator } from "@rocicorp/zero";
import { dmActionsMutators } from "./actions-dm";
import { bootstrapActionsMutators } from "./actions-bootstrap";
import { inviteActionsMutators } from "./actions-invites";
import { engagementActionMutators } from "./actions-engagement";
import { membershipActionMutators } from "./actions-membership";
import { notificationActionMutators } from "./actions-notifications";
import {
  actionSendMessageSchema,
  actionSendMessageCore,
  actionReplyToThreadSchema,
  actionReplyToThreadCore,
  actionEditMessageSchema,
  actionEditMessageCore,
  actionSendMessageWithAttachmentsSchema,
  actionSendMessageWithAttachmentsCore,
  actionReplyToThreadWithAttachmentsSchema,
  actionReplyToThreadWithAttachmentsCore,
} from "./actions-message-cores";
import {
  actionSendCallEventSchema,
  actionSendCallEventCore,
  actionPostSystemLogSchema,
  actionPostSystemLogCore,
  actionCreateThreadSchema,
  actionCreateThreadCore,
  markChannelReadSchema,
  markChannelReadCore,
} from "./actions-event-cores";

// ── D2.0: schemas + cores re-exported so the server-mutator extension
//   (`server/server-mutators.ts`) and the mutators barrel keep importing
//   them from `./actions` unchanged. Implementations live in the
//   `actions-{message,event}-cores.ts` siblings. ──
export {
  actionSendMessageSchema,
  actionSendMessageCore,
  actionReplyToThreadSchema,
  actionReplyToThreadCore,
  actionEditMessageSchema,
  actionEditMessageCore,
  actionSendMessageWithAttachmentsSchema,
  actionSendMessageWithAttachmentsCore,
  actionReplyToThreadWithAttachmentsSchema,
  actionReplyToThreadWithAttachmentsCore,
} from "./actions-message-cores";
export {
  actionSendCallEventSchema,
  actionSendCallEventCore,
  actionPostSystemLogSchema,
  actionPostSystemLogCore,
  actionCreateThreadSchema,
  actionCreateThreadCore,
  markChannelReadSchema,
  markChannelReadCore,
} from "./actions-event-cores";

// Bundled multi-step "action" mutators. The heavy implementations live in
// topical siblings — message/event cores (`actions-{message,event}-cores.ts`),
// engagement/membership/notification inline mutators (`actions-{engagement,
// membership,notifications}.ts`), and the DM/bootstrap/invite bundles
// (`actions-{dm,bootstrap,invites}.ts`). This file is the registry that
// assembles them, plus the thin client-side wrappers for the core-backed
// message/event actions (the server-mutator extension overrides these to add
// post-insert fan-out).
export const actionsMutators = {
  // ── DM + system-log channel bootstrap (see actions-dm.ts) ──────────────
  ...dmActionsMutators,

  // ── Workspace + channel-with-members bootstrap (see actions-bootstrap.ts) ─
  ...bootstrapActionsMutators,

  // ── B-2: invite accept (see actions-invites.ts). All real work lives in
  //   the server-mutator override; the shared shell is intentionally a no-op.
  ...inviteActionsMutators,

  // Send a new message to a channel
  sendMessage: defineMutator(actionSendMessageSchema, async (params) => {
    await actionSendMessageCore(params);
  }),

  // Reply to a thread (creates message with thread_id)
  replyToThread: defineMutator(actionReplyToThreadSchema, async (params) => {
    await actionReplyToThreadCore(params);
  }),

  // Phase 7b PR D1 — atomic message+attachments variants (iOS path).
  // Bundles the message INSERT + N attachment INSERTs into one Zero
  // transaction so partial failures roll back both. Server override in
  // `server/server-mutators.ts` runs `applyMessagePostInsertEffects`
  // after the core completes.
  sendMessageWithAttachments: defineMutator(
    actionSendMessageWithAttachmentsSchema,
    async (params) => {
      await actionSendMessageWithAttachmentsCore(params);
    },
  ),
  replyToThreadWithAttachments: defineMutator(
    actionReplyToThreadWithAttachmentsSchema,
    async (params) => {
      await actionReplyToThreadWithAttachmentsCore(params);
    },
  ),

  // Edit a message — content and/or payload edits re-derive preview_text +
  // structured columns via the builder. `kind` is intentionally NOT accepted
  // (immutable after insert).
  editMessage: defineMutator(actionEditMessageSchema, async (params) => {
    await actionEditMessageCore(params);
  }),

  // Mark channel as read. Canonical write entry point for mark-read across
  // all surfaces. The shared core is a no-op on the client; a server-mutator
  // extension writes `user_channel_stats.last_read_at` via raw SQL.
  markChannelRead: defineMutator(markChannelReadSchema, async (params) => {
    await markChannelReadCore(params);
  }),

  // Create a thread rooted at `messageId` — posts a system announcement;
  // the source message itself is unchanged.
  createThread: defineMutator(actionCreateThreadSchema, async (params) => {
    await actionCreateThreadCore(params);
  }),

  // Send a call event system message (typed call_* kind).
  sendCallEvent: defineMutator(actionSendCallEventSchema, async (params) => {
    await actionSendCallEventCore(params);
  }),

  // Post a system log message as the system-bot identity (kind='system').
  postSystemLog: defineMutator(actionPostSystemLogSchema, async (params) => {
    await actionPostSystemLogCore(params);
  }),

  // ── Engagement: reactions, pins, thread-follow (actions-engagement.ts) ──
  ...engagementActionMutators,

  // ── Channel membership + ownership transfer (actions-membership.ts) ──────
  ...membershipActionMutators,

  // ── Notification read-state (actions-notifications.ts) ───────────────────
  ...notificationActionMutators,
};
